<template>
    <div>
        <form @submit.prevent="save">
            <div class="row">
                <div class="col-md-4">
                    <div class="form-group">
                        <label>Return Date</label>
                        <input
                            v-model="form.return_date"
                            type="date"
                            class="form-control"
                            :class="{ 'is-invalid': errors.return_date }"
                        />
                        <div v-if="errors.return_date" class="text-danger">
                            {{ errors.return_date[0] }}
                        </div>
                    </div>
                </div>
                <div class="col-md-4">
                    <div class="form-group">
                        <label>Reference Number</label>
                        <input
                            v-model="form.reference_number"
                            type="text"
                            class="form-control"
                            :class="{ 'is-invalid': errors.reference_number }"
                            readonly
                        />
                        <div v-if="errors.reference_number" class="text-danger">
                            {{ errors.reference_number[0] }}
                        </div>
                    </div>
                </div>
                <div class="col-md-4">
                    <div class="form-group">
                        <label>Reason</label>
                        <textarea
                            v-model="form.reason"
                            class="form-control"
                            :class="{ 'is-invalid': errors.reason }"
                            rows="1"
                            placeholder="Enter return reason"
                        ></textarea>
                        <div v-if="errors.reason" class="text-danger">
                            {{ errors.reason[0] }}
                        </div>
                    </div>
                </div>
            </div>

            <div class="table-responsive mt-3">
                <table class="table table-bordered">
                    <thead>
                        <tr>
                            <th>Product</th>
                            <th>Sold Quantity</th>
                            <th>Return Quantity</th>
                            <th>Unit Price</th>
                            <th>Subtotal</th>
                            <th>Note</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="detail in saleDetails"
                            :key="detail.product_id"
                        >
                            <td>{{ detail.product_name }}</td>
                            <td>{{ detail.quantity }}</td>
                            <td>
                                <input
                                    v-model.number="
                                        form.quantities[detail.product_id]
                                    "
                                    @input="updateSubtotal(detail.product_id)"
                                    type="number"
                                    class="form-control"
                                    :class="{
                                        'is-invalid':
                                            errors[
                                                `quantities.${detail.product_id}`
                                            ],
                                    }"
                                    min="0"
                                    :max="form.original_qty[detail.product_id] || detail.quantity"
                                />
                                <div
                                    v-if="
                                        errors[
                                            `quantities.${detail.product_id}`
                                        ]
                                    "
                                    class="text-danger"
                                >
                                    {{
                                        errors[
                                            `quantities.${detail.product_id}`
                                        ][0]
                                    }}
                                </div>
                            </td>
                            <td>
                                <input
                                    v-model.number="
                                        form.unit_prices[detail.product_id]
                                    "
                                    @input="updateSubtotal(detail.product_id)"
                                    type="number"
                                    class="form-control"
                                    step="0.01"
                                    readonly
                                />
                            </td>
                            <td>
                                ${{
                                    (
                                        form.subtotals[detail.product_id] || 0
                                    ).toFixed(2)
                                }}
                            </td>
                            <td>
                                <input
                                    v-model="form.notes[detail.product_id]"
                                    type="text"
                                    class="form-control"
                                    placeholder="Note"
                                />
                            </td>
                        </tr>
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colspan="4" class="text-right">
                                <strong>Total Amount:</strong>
                            </td>
                            <td colspan="2">
                                ${{ totals.total_amount.toFixed(2) }}
                            </td>
                        </tr>
                        <tr>
                            <td colspan="4" class="text-right">
                                <strong>Return Amount:</strong>
                            </td>
                            <td colspan="2">
                                ${{ totals.returnable_amount.toFixed(2) }}
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            <div class="row mt-3">
                <div class="col-md-12">
                    <button
                        type="submit"
                        class="btn btn-primary"
                        :disabled="loading"
                    >
                        {{ loading ? "Creating Return..." : "Create Return" }}
                    </button>
                    <button
                        type="button"
                        class="btn btn-secondary ms-2"
                        @click="cancel"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import { showSuccessMessage, showErrorMessage } from '../utils/notifications.js';

export default {
    name: "SaleReturnForm",
    props: {
        sale: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            form: {
                return_date: new Date().toISOString().split("T")[0],
                reference_number: "SR-" + Date.now(),
                reason: "",
                quantities: {},
                unit_prices: {},
                unit_costs: {},
                subtotals: {},
                notes: {},
                original_qty: {},
            },
            saleDetails: [],
            errors: {},
            loading: false,
        };
    },
    computed: {
        totals() {
            const total_amount = Object.values(this.form.subtotals).reduce(
                (sum, subtotal) => sum + (subtotal || 0),
                0
            );
            const total_cost = Object.keys(this.form.quantities).reduce(
                (sum, productId) => {
                    const qty = this.form.quantities[productId] || 0;
                    const cost = this.form.unit_costs[productId] || 0;
                    return sum + qty * cost;
                },
                0
            );

            // Ensure return amount doesn't exceed paid amount
            const returnable_amount = Math.min(
                total_amount,
                this.sale.paid_amount || 0
            );

            return {
                total_amount,
                total_cost,
                returnable_amount,
            };
        },
    },
    methods: {
        initializeForm() {
            // Initialize form data from sale details
            this.saleDetails = this.sale.sale_details || [];

            this.saleDetails.forEach((detail) => {
                this.form.quantities[detail.product_id] = 0;
                this.form.original_qty[detail.product_id] = detail.quantity;
                this.form.unit_prices[detail.product_id] = parseFloat(detail.unit_price);
                this.form.unit_costs[detail.product_id] = parseFloat(detail.unit_cost);
                this.form.subtotals[detail.product_id] = 0;
                this.form.notes[detail.product_id] = "";
            });
        },

        async loadSaleData() {
            try {
                const response = await this.$http.get(
                    `/api/sale-returns/sale/${this.sale.id}`
                );
                if (response.data.success) {
                    // Update sale data with full details
                    Object.assign(this.sale, response.data.sale);
                    this.initializeForm();
                }
            } catch (error) {
                console.error("Failed to load sale data:", error);
                this.showErrorMessage("Failed to load sale data");
            }
        },
        updateSubtotal(productId) {
            const quantity = this.form.quantities[productId] || 0;
            const originalQty = this.form.original_qty[productId] || 0;
            const unitPrice = this.form.unit_prices[productId] || 0;

            // Validate quantity doesn't exceed original sold quantity
            if (quantity > originalQty) {
                this.showErrorMessage(`Return quantity cannot exceed sold quantity of ${originalQty}`);
                this.form.quantities[productId] = originalQty;
                this.form.subtotals[productId] = originalQty * unitPrice;
            } else {
                this.form.subtotals[productId] = quantity * unitPrice;
            }
        },

        async save() {
            this.loading = true;
            this.errors = {};

            try {
                // Prepare return data
                const returnData = {
                    sale_id: this.sale.id,
                    return_date: this.form.return_date,
                    reference_number: this.form.reference_number,
                    reason: this.form.reason,
                    items: this.saleDetails
                        .filter(
                            (detail) =>
                                (this.form.quantities[detail.product_id] || 0) >
                                0
                        )
                        .map((detail) => ({
                            product_id: detail.product_id,
                            quantity: this.form.quantities[detail.product_id],
                            unit_price:
                                this.form.unit_prices[detail.product_id],
                            unit_cost: this.form.unit_costs[detail.product_id],
                            batch_number: detail.batch_number,
                            note: this.form.notes[detail.product_id] || "",
                        })),
                    total_amount: this.totals.total_amount,
                    total_cost: this.totals.total_cost,
                    returnable_amount: this.totals.returnable_amount,
                };

                if (returnData.items.length === 0) {
                    this.showErrorMessage(
                        "Please select at least one item to return."
                    );
                    return;
                }

                const response = await this.$http.post(
                    "/api/sale-returns",
                    returnData
                );

                this.showSuccessMessage("Sale return created successfully!");

                // Emit success event
                this.$emit("return-created", response.data);

                // Redirect
                if (response.data.redirect_url) {
                    setTimeout(() => {
                        window.location.href = response.data.redirect_url;
                    }, 1500);
                }
            } catch (error) {
                console.error("Sale return creation error:", error);

                if (error.response && error.response.status === 422) {
                    this.errors = error.response.data.errors;
                } else {
                    this.showErrorMessage(
                        "An error occurred while creating the sale return."
                    );
                }
            } finally {
                this.loading = false;
            }
        },

        cancel() {
            this.$emit("cancel");
            // Navigate back or close modal
            if (this.sale.id) {
                window.location.href = `/sale/list`;
            }
        },

        showSuccessMessage,
        showErrorMessage,
    },

    async mounted() {
        console.log("SaleReturnForm component mounted");
        console.log("Sale data:", this.sale);

        // Load full sale data if needed
        if (
            this.sale.id &&
            (!this.sale.sale_details || this.sale.sale_details.length === 0)
        ) {
            await this.loadSaleData();
        } else {
            this.initializeForm();
        }

        // Mark element as mounted
        this.$el.closest("[data-vue-component]")?.classList.add("vue-mounted");
    },
};
</script>

<style scoped>
.btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.text-danger {
    font-size: 0.875rem;
    margin-top: 0.25rem;
}

.form-control.is-invalid {
    border-color: #dc3545;
}

.table th {
    background-color: #f8f9fa;
    font-weight: 600;
}

.table tfoot td {
    font-weight: 600;
    background-color: #f8f9fa;
}
</style>
